import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { apiUrl } from '../lib/apiBase';
import { getEcho, onEchoConnectionChange } from '../lib/echo';
import { playNewChatMessageSound } from '../../../shared/useSound';
import { useClientAuthStore } from './auth';
import { useAppMetaStore } from './appMeta';

// Anexo recebido (T-MEDIA): `url` já vem proxiada+assinada pelo backend.
export interface ChatAttachment {
    file_type: string;
    url: string;
    name?: string | null;
}

export interface ChatMessage {
    id: string | number;
    content: string;
    message_type?: string;
    created_at?: string | null;
    attachments?: ChatAttachment[];
    _pending?: boolean;
    _failed?: boolean;
}

export type ChatTopicType = 'order' | 'question' | 'issue' | 'other';

export interface ChatConversationSummary {
    id: string;
    status: string;
    last_activity_at: string | number | null;
    last_message: string;
    last_message_outgoing: boolean;
    unread: number;
    // Carimbo do espelho local (D-CHAT-18 / T11) — tópico/título/pedido do episódio.
    topic_type: ChatTopicType | null;
    title: string | null;
    order_id: string | null;
    order_number: string | null;
}

/** Rascunho do episódio a criar (D-CHAT-18): chip + pedido escolhidos no starter,
 *  staged no cliente até o 1º envio (que cria e carimba a conversa). */
export interface ChatDraft {
    topicType: ChatTopicType;
    orderId: string | null;
    orderNumber: string | null;
}

/** Pedido selecionável no seletor do chip "order" (reusa o histórico storefront/orders). */
export interface SelectableOrder {
    id: string;
    order_number: string | null;
    status: string;
    total: number;
    created_at: string | null;
    items_summary: string;
}

/** Episódio ENCERRADO lido read-only (D-CHAT-18) — não mistura com o estado da conversa ativa. */
export interface ChatEpisode {
    conversation_id: string;
    status: string;
    can_reply: boolean;
    topic_type: ChatTopicType | null;
    title: string | null;
    order_id: string | null;
    order_number: string | null;
    data: ChatMessage[];
}

interface ChatMessageReceivedPayload {
    conversation_id: string;
    channel_type: string;
    message: { id: string | number; content: string; type: string | null; created_at: string | null; attachments?: ChatAttachment[] };
    customer: { id: string; name: string } | null;
}

// T-MEDIA: rótulo de mídia p/ o preview do inbox quando o content é vazio.
function mediaLabel(atts?: ChatAttachment[]): string {
    if (!atts || !atts.length) return '';
    switch (atts[0].file_type) {
        case 'image': return '📷 Foto';
        case 'audio': return '🎤 Áudio';
        case 'video': return '🎬 Vídeo';
        default: return '📎 Arquivo';
    }
}

function authHeaders(): Record<string, string> {
    const auth = useClientAuthStore();
    const meta = useAppMetaStore();
    return {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token ?? ''}`,
        'X-Tenant-ID': meta.tenantId,
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EchoChannel = any;
let tempSeq = 0;

/**
 * Ao criar uma conversa Web, o widget do CRM injeta mensagens canned
 * (welcome "Ola, como podemos…", email-collect "Get notified by email") como
 * `message_type: 'template'`. Não usamos o widget — é ruído. No canal Web o
 * operador só manda texto livre (`outgoing`), nunca `template`, então descartar
 * `template` aqui é seguro e limpa o fio do cliente.
 */
function withoutWidgetNoise(list: ChatMessage[]): ChatMessage[] {
    // 'template' = canned do widget; 'activity' = evento de sistema do CRM
    // ("Conversation was marked resolved by…", em inglês/interno) — o cliente
    // não deve ver nenhum dos dois no fio.
    return list.filter(m => m.message_type !== 'template' && m.message_type !== 'activity');
}

function msgTs(m: ChatMessage): number {
    if (!m.created_at) return 0;
    const t = Date.parse(m.created_at);

    return Number.isNaN(t) ? 0 : t;
}

/**
 * Ordena mais-antiga→mais-recente (a thread renderiza de cima pra baixo). O CRM
 * não garante ordem estável entre os endpoints (o `.reverse()` cru invertia
 * errado quando `data` já vinha oldest-first), então ordenamos por `created_at`.
 */
function sortedAsc(list: ChatMessage[]): ChatMessage[] {
    return [...list].sort((a, b) => msgTs(a) - msgTs(b));
}

/**
 * Chat do cliente (canal Web, D-CHAT-11/12) — uma única conversa ativa (a do
 * próprio cliente logado), diferente da store do admin que lista várias.
 * Fetch via /api/v1/pwa/chat/* (T-PWA-BE) — nunca guarda/usa um
 * conversation_id vindo de outro lugar que não a própria resposta do backend.
 */
export const usePwaChatStore = defineStore('pwa-chat', () => {
    const conversationId = ref<string | null>(null);
    const messages = ref<ChatMessage[]>([]);
    const loading = ref(false);
    const sending = ref(false);
    const error = ref<'crm_unavailable' | 'error' | null>(null);
    const unreadCount = ref(0);
    const connected = ref(false);

    // Inbox (lista de episódios do cliente com a loja — CHAT-29).
    const conversations = ref<ChatConversationSummary[]>([]);
    const conversationsLoading = ref(false);
    const conversationsError = ref<'crm_unavailable' | 'error' | null>(null);

    // Rascunho do episódio novo (staged até o 1º envio — D-CHAT-18).
    const draft = ref<ChatDraft | null>(null);

    // Marca de "visto pelo cliente" por conversa (localStorage). O unread do
    // cliente é sobre mensagens do OPERADOR (outgoing) não vistas — NÃO o
    // `unread_count` do CRM, que é agente-cêntrico e conta as mensagens do
    // próprio cliente (incoming) como não-lidas.
    const SEEN_KEY = 'pwa-chat-seen';
    const seen = ref<Record<string, number>>((() => {
        try {
            return JSON.parse(window.localStorage.getItem(SEEN_KEY) ?? '{}') as Record<string, number>;
        } catch {
            return {};
        }
    })());

    let channel: EchoChannel = null;
    let echoConnectionUnsub: (() => void) | null = null;
    let customerWatchStop: (() => void) | null = null;
    let pollHandle: ReturnType<typeof setInterval> | null = null;
    let opened = false;

    const hasMessages = computed(() => messages.value.length > 0);
    const hasConversations = computed(() => conversations.value.length > 0);
    // Single-active (D-CHAT-18): no máx. 1 conversa aberta. "Nova conversa" só
    // aparece quando NÃO há ativa (todas encerradas). O gate de UI usa isto.
    const activeConversation = computed(() => conversations.value.find(c => c.status !== 'resolved') ?? null);
    const hasActiveConversation = computed(() => activeConversation.value !== null);

    /** Inbox — lista os episódios de conversa do cliente com a loja (CHAT-29). */
    async function fetchConversations(): Promise<void> {
        conversationsLoading.value = true;
        conversationsError.value = null;
        try {
            const res = await window.fetch(apiUrl('pwa/chat/conversations'), { headers: authHeaders() });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                conversationsError.value = body.code === 'crm_unavailable' ? 'crm_unavailable' : 'error';
                return;
            }
            const json = (await res.json()) as { conversations: ChatConversationSummary[] };
            conversations.value = json.conversations ?? [];
        } catch {
            conversationsError.value = 'error';
        } finally {
            conversationsLoading.value = false;
        }
    }

    function tsSeconds(value: string | number | null): number {
        if (value === null) return 0;
        if (typeof value === 'number') return value;
        const t = Date.parse(value);

        return Number.isNaN(t) ? 0 : Math.floor(t / 1000);
    }

    /** Registra que o cliente viu a conversa até agora (limpa o unread no inbox). */
    function markConversationSeen(id: string): void {
        if (!id) return;
        seen.value = { ...seen.value, [id]: Math.floor(Date.now() / 1000) };
        try {
            window.localStorage.setItem(SEEN_KEY, JSON.stringify(seen.value));
        } catch {
            // storage indisponível/cheio — o unread apenas não persiste entre sessões
        }
    }

    /** Não-lida do cliente: última msg foi do operador e chegou depois do último "visto". */
    function isConversationUnread(c: ChatConversationSummary): boolean {
        if (!c.last_message_outgoing) return false;

        return tsSeconds(c.last_activity_at) > (seen.value[c.id] ?? 0);
    }

    /**
     * Atualiza o INBOX ao vivo quando chega mensagem por realtime: sobe a conversa
     * pro topo com o novo preview/horário/direção (→ dispara o unread se for do
     * operador). Conversa ainda não listada (1ª mensagem de um episódio) → recarrega.
     */
    function touchConversationFromRealtime(payload: ChatMessageReceivedPayload, outgoing: boolean): void {
        const idx = conversations.value.findIndex(c => c.id === payload.conversation_id);
        if (idx < 0) {
            void fetchConversations();

            return;
        }
        const updated: ChatConversationSummary = {
            ...conversations.value[idx],
            last_message: payload.message.content || mediaLabel(payload.message.attachments),
            last_message_outgoing: outgoing,
            last_activity_at: Math.floor(Date.now() / 1000),
        };
        conversations.value = [updated, ...conversations.value.filter((_, i) => i !== idx)];
    }

    function setDraft(d: ChatDraft): void {
        draft.value = d;
    }

    function clearDraft(): void {
        draft.value = null;
    }

    /**
     * Lê um episódio ENCERRADO específico read-only (D-CHAT-18). Backend autoriza
     * o dono. Não muta o estado da conversa ativa — a tela guarda o retorno local.
     */
    async function fetchEpisode(id: string): Promise<ChatEpisode | null> {
        try {
            const res = await window.fetch(apiUrl(`pwa/chat/episodes/${id}`), { headers: authHeaders() });
            if (!res.ok) return null;

            const ep = (await res.json()) as ChatEpisode;
            ep.data = sortedAsc(withoutWidgetNoise(ep.data ?? []));

            return ep;
        } catch {
            return null;
        }
    }

    async function fetchConversation(): Promise<void> {
        loading.value = true;
        error.value = null;
        try {
            const res = await window.fetch(apiUrl('pwa/chat/conversation'), { headers: authHeaders() });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                error.value = body.code === 'crm_unavailable' ? 'crm_unavailable' : 'error';
                return;
            }
            const json = (await res.json()) as { conversation_id: string | null; data: ChatMessage[] };
            conversationId.value = json.conversation_id; // null quando ainda não há conversa (find-only, D-CHAT-18)
            messages.value = sortedAsc(withoutWidgetNoise(json.data));
        } catch {
            error.value = 'error';
        } finally {
            loading.value = false;
        }
    }

    async function loadOlderMessages(): Promise<void> {
        const oldest = messages.value[0];
        if (!oldest) return;
        try {
            const res = await window.fetch(apiUrl(`pwa/chat/conversation/messages?before=${oldest.id}`), { headers: authHeaders() });
            if (!res.ok) return;
            const json = (await res.json()) as { data: ChatMessage[] };
            if (json.data.length === 0) return;
            const older = withoutWidgetNoise(json.data);
            if (older.length > 0) messages.value = sortedAsc([...older, ...messages.value]);
        } catch {
            // silencioso — "carregar mais" simplesmente não avança
        }
    }

    function upsertMessage(message: ChatMessage, { markUnread = true }: { markUnread?: boolean } = {}): void {
        messages.value = [...messages.value.filter(m => String(m.id) !== String(message.id)), message];
        if (markUnread && !opened) unreadCount.value += 1;
    }

    function removeMessage(id: string): void {
        messages.value = messages.value.filter(m => String(m.id) !== id);
    }

    /** Marca a thread como aberta/lida (chamada pela tela que exibe a conversa). */
    function markOpened(): void {
        opened = true;
        unreadCount.value = 0;
    }

    function markClosed(): void {
        opened = false;
    }

    /**
     * Zera o buffer da conversa ATIVA (mensagens + id). Ao entrar numa conversa
     * NOVA (D-CHAT-18) a thread tem de começar vazia; como `messages` vive em
     * memória e o SPA não recarrega entre navegações, sem este reset o fio da
     * conversa anterior (ex.: uma que já foi encerrada/apagada) vaza pro episódio
     * novo. Não mexe no inbox (`conversations`) nem no draft.
     */
    function resetActiveThread(): void {
        messages.value = [];
        conversationId.value = null;
        error.value = null;
    }

    /**
     * Envia mensagem incoming. No 1º envio de um episódio novo (D-CHAT-18), o chip
     * (`topicType`) e o pedido (`orderId`) ficam *staged* e viajam junto — o
     * backend carimba o espelho local nesse momento. Envios subsequentes vão sem
     * eles (o carimbo é imutável). A resposta traz `{ message, conversation_id, meta }`.
     */
    async function sendMessage(
        content: string,
        opts: { topicType?: ChatTopicType; orderId?: string | null } = {},
    ): Promise<void> {
        if (!content.trim()) return;

        const tempId = `temp-${++tempSeq}`;
        upsertMessage({ id: tempId, content, message_type: 'incoming', created_at: new Date().toISOString(), _pending: true }, { markUnread: false });

        sending.value = true;
        try {
            const res = await window.fetch(apiUrl('pwa/chat/messages'), {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    content,
                    ...(opts.topicType ? { topic_type: opts.topicType } : {}),
                    ...(opts.orderId ? { order_id: opts.orderId } : {}),
                }),
            });
            if (!res.ok) {
                messages.value = messages.value.map(m => (m.id === tempId ? { ...m, _pending: false, _failed: true } : m));
                return;
            }
            const json = (await res.json()) as { message: ChatMessage; conversation_id: string };
            removeMessage(tempId);
            upsertMessage(json.message, { markUnread: false });
            if (json.conversation_id) conversationId.value = json.conversation_id;
        } catch {
            messages.value = messages.value.map(m => (m.id === tempId ? { ...m, _pending: false, _failed: true } : m));
        } finally {
            sending.value = false;
        }
    }

    function startPolling(): void {
        stopPolling();
        pollHandle = setInterval(() => { void fetchConversation(); }, 15_000);
    }

    function stopPolling(): void {
        if (pollHandle !== null) {
            clearInterval(pollHandle);
            pollHandle = null;
        }
    }

    /**
     * Assina `chat.customer.{customerId}` (CHAT-31). NÃO chama `disconnectEcho()`
     * no unsubscribe — o Echo do PWA cliente é um singleton global também usado
     * por `useOrderTracking`; desconectá-lo aqui derrubaria a assinatura de
     * outra tela montada ao mesmo tempo (ex.: sheet de chat sobre o
     * acompanhamento do pedido). Só paramos de ouvir o nosso canal.
     */
    function subscribeRealtime(): void {
        const auth = useClientAuthStore();

        const attach = (customerId: string): void => {
            const echo = getEcho(() => {
                const a = useClientAuthStore();
                const meta = useAppMetaStore();
                return { Authorization: `Bearer ${a.token ?? ''}`, 'X-Tenant-ID': meta.tenantId };
            });
            if (!echo) return;

            channel = echo.private(`chat.customer.${customerId}`)
                .listen('.ChatMessageReceived', (payload: ChatMessageReceivedPayload) => {
                    // ruído do widget ('template') e eventos de sistema ('activity',
                    // ex.: "marked resolved by…") não entram no fio do cliente.
                    if (payload.message.type === 'template' || payload.message.type === 'activity') return;
                    const outgoing = payload.message.type === 'outgoing';
                    // Som só na mensagem do OPERADOR (outgoing) — nunca no eco da
                    // própria mensagem do cliente (incoming).
                    if (outgoing) playNewChatMessageSound();
                    upsertMessage({
                        id: payload.message.id,
                        content: payload.message.content,
                        message_type: payload.message.type ?? undefined,
                        created_at: payload.message.created_at,
                        attachments: payload.message.attachments,
                    }, { markUnread: outgoing }); // só a msg do operador conta como não-lida
                    touchConversationFromRealtime(payload, outgoing);
                });
        };

        const customerId = auth.customer?.id;
        if (customerId) {
            attach(customerId);
        } else {
            // `auth.customer` só existe após o fetch do cliente (assíncrono). Se o
            // chat monta antes disso (comum), o subscribe saía sem assinar e o
            // tempo real só vinha no reload — mesmo bug do admin. Assina quando o
            // cliente aparecer.
            customerWatchStop = watch(() => auth.customer?.id, (id) => {
                if (id) {
                    customerWatchStop?.();
                    customerWatchStop = null;
                    attach(id);
                }
            });
        }

        echoConnectionUnsub = onEchoConnectionChange((isConnected) => {
            connected.value = isConnected;
            if (isConnected) stopPolling();
            else startPolling();
        });
    }

    function unsubscribe(): void {
        if (customerWatchStop) {
            customerWatchStop();
            customerWatchStop = null;
        }
        if (channel) {
            channel.stopListening('.ChatMessageReceived');
            channel = null;
        }
        if (echoConnectionUnsub) {
            echoConnectionUnsub();
            echoConnectionUnsub = null;
        }
        stopPolling();
    }

    return {
        conversationId, messages, loading, sending, error, unreadCount, connected, hasMessages,
        conversations, conversationsLoading, conversationsError, hasConversations,
        activeConversation, hasActiveConversation, fetchConversations,
        markConversationSeen, isConversationUnread,
        draft, setDraft, clearDraft, fetchEpisode,
        fetchConversation, loadOlderMessages, sendMessage,
        markOpened, markClosed, resetActiveThread,
        subscribeRealtime, unsubscribe,
    };
});
